"use client";

import * as React from "react";
import { useRouter } from "@tanstack/react-router";
import {
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeleteOrganisationDialog } from "@/components/delete-organisation-dialog";
import {
  AddOrganisationDialog,
  EditOrganisationDialog,
} from "@/features/organisation/components";
import { useDeleteOrganisation } from "@/hooks/use-organisations";

// Organisation schema
export const organisationSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Organisation = z.infer<typeof organisationSchema>;

interface OrganisationsTableProps {
  data: Organisation[];
  isLoading?: boolean;
  error?: string | null;
  totalCount?: number;
  pageIndex: number;
  pageSize: number;
  searchQuery: string;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearchChange: (search: string) => void;
}

export function OrganisationsTable({
  data,
  isLoading = false,
  error = null,
  totalCount = 0,
  pageIndex,
  pageSize,
  searchQuery,
  onPageChange,
  onPageSizeChange: _onPageSizeChange,
  onSearchChange,
}: OrganisationsTableProps) {
  const router = useRouter();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [editingOrganisation, setEditingOrganisation] =
    React.useState<Organisation | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [deletingOrganisation, setDeletingOrganisation] =
    React.useState<Organisation | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const deleteMutation = useDeleteOrganisation();

  const handleEditClick = (organisation: Organisation) => {
    setEditingOrganisation(organisation);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (organisation: Organisation) => {
    setDeletingOrganisation(organisation);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!deletingOrganisation) return;

    deleteMutation.mutate(deletingOrganisation.id, {
      onSuccess: () => {
        toast.success(
          `Organisation "${deletingOrganisation.name}" deleted successfully!`
        );
        setIsDeleteDialogOpen(false);
        setDeletingOrganisation(null);
      },
      onError: (error) => {
        const errorMessage =
          typeof error === "string"
            ? error
            : error?.message || "Failed to delete organisation";
        toast.error(errorMessage);
      },
    });
  };

  // Column definitions
  const columns: ColumnDef<Organisation>[] = React.useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Organisation Name",
        cell: ({ row }) => (
          <div className="font-medium truncate max-w-xs" title={row.getValue("name") as string}>{row.getValue("name")}</div>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ row }) => {
          const date = new Date(row.getValue("createdAt"));
          return (
            <div className="text-muted-foreground">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const organisation = row.original;

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="data-[state=open]:bg-muted size-8"
                  size="icon"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDotsVertical className="size-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(organisation);
                  }}
                >
                  <IconEdit className="mr-2 size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(organisation);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <IconTrash className="mr-2 size-4" />
                  {deleteMutation.isPending ? "Deleting..." : "Delete"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(totalCount / pageSize),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
  });

  const totalPages = Math.ceil(totalCount / pageSize);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;

  return (
    <div className="w-full space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between">
        <Input
          placeholder="Search organisations..."
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          className="max-w-sm"
          disabled={isLoading}
        />
        <AddOrganisationDialog>
          <Button>
            <IconPlus className="mr-2 size-4" />
            Add Organisation
          </Button>
        </AddOrganisationDialog>
      </div>

      {/* Edit Organisation Dialog */}
      {editingOrganisation && (
        <EditOrganisationDialog
          organisation={editingOrganisation}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
        />
      )}

      {/* Delete Organisation Dialog */}
      <DeleteOrganisationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        organisationName={deletingOrganisation?.name}
        isDeleting={deleteMutation.isPending}
      />

      {/* Error State */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Error loading organisations: {error}
          </p>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="ml-2 text-muted-foreground">
                      Loading organisations...
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => router.navigate({ to: `/organisations/$organisationId`, params: { organisationId: row.original.id } })}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    {searchQuery
                      ? "No organisations found matching your search."
                      : "No organisations found. Add one to get started."}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-muted-foreground flex-1 text-sm">
          Showing {data.length > 0 ? pageIndex * pageSize + 1 : 0} to{" "}
          {Math.min((pageIndex + 1) * pageSize, totalCount)} of {totalCount}{" "}
          organisations
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={!canPreviousPage || isLoading}
          >
            Previous
          </Button>
          <div className="text-sm font-medium">
            Page {totalPages > 0 ? pageIndex + 1 : 0} of {totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={!canNextPage || isLoading}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
